import type { Observable as ObservableType } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import { of } from 'rxjs/observable/of';
import { catchError, map } from 'rxjs/operators';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	fetchConfluencePermissions,
	fetchConfluenceSpace,
	fetchUserProperties,
	setUserProperties,
} from '../../rest/whiteboards-entrypoint-experiment';
import {
	setHasViewedPulseFailure,
	setHasViewedPulseSuccess,
	type SetHasViewedPulseFailureAction,
	type SetHasViewedPulseSuccessAction,
} from '../../state/actions/whiteboards-entrypoint-experiment';
import { getAtlassianAccountId } from '../../state/selectors/app';
import type { MiddlewareAPI } from '../../state/types';

export const loadConfluencePermissionsService = (): ObservableType<boolean> =>
	fetchConfluencePermissions().pipe(
		map((response) => !!response.operations && response.operations.length > 0), // Map response to boolean
		catchError(() => of(false)), // Catch errors and return false
	);

export const loadConfluenceSpaceService = (): ObservableType<boolean> =>
	fetchConfluenceSpace().pipe(
		map((response) => !!response.results && response.results.length > 0), // Map response to boolean
		catchError(() => of(false)), // Catch errors and return false
	);

export const loadHasViewedPulseService = (accountId: string): ObservableType<boolean> =>
	fetchUserProperties(accountId).pipe(
		map((res) => res.value),
		catchError((error) => {
			// 404: API deliberately returns 404 if user property is not found
			if (error.statusCode === 404) {
				return of(false);
			}

			fireErrorAnalytics({
				error,
				meta: {
					id: 'backlog.load-has-viewed-pulse-service.failure',
					packageName: 'jiraSoftwareBacklog',
					teamName: 'a4t-tanuki',
				},
			});
			return of(true);
		}),
	);

export const setHasViewedPulseService = ({
	store,
}: {
	store: MiddlewareAPI;
}): ObservableType<SetHasViewedPulseSuccessAction | SetHasViewedPulseFailureAction> => {
	const accountId = getAtlassianAccountId(store.getState());
	return setUserProperties(accountId ?? '', true).pipe(
		map(() => setHasViewedPulseSuccess(true)),
		catchError(() => of(setHasViewedPulseFailure())),
	);
};
